import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Project, ProjectPatch } from '../auth/types';
import type { CompanionDescriptor } from '../brain/persona';
import { getStoredToken } from './useAuth';

function buildHeaders(): Record<string, string> {
  return {
    Authorization: `Bearer ${getStoredToken() ?? ''}`,
    'Content-Type': 'application/json',
  };
}

function sortProjects(rows: Project[]): Project[] {
  return rows.slice().sort(
    (a, b) => new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime(),
  );
}

function isProject(value: unknown): value is Project {
  if (!value || typeof value !== 'object') return false;
  const row = value as Partial<Project>;
  return typeof row.id === 'string'
    && typeof row.identity === 'string'
    && typeof row.name === 'string'
    && typeof row.created_at === 'string'
    && typeof row.last_activity === 'string'
    && typeof row.archived === 'number';
}

/**
 * Shared studio-project client. `identity` on a project is creator provenance,
 * not visibility or mutation authority.
 */
export function useProjects(companions: CompanionDescriptor[] | null | undefined) {
  const [projects, setProjects] = useState<Project[]>([]);
  const projectsRef = useRef<Project[]>([]);
  projectsRef.current = projects;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const listRequestVersion = useRef(0);
  const mutationsInFlight = useRef(0);
  const refreshQueued = useRef(false);

  const companionIds = useMemo(
    () => companions
      ? Array.from(new Set(companions.map((companion) => companion.id).filter(Boolean))).sort()
      : null,
    [companions],
  );
  const companionKey = companionIds?.join('\u0000') ?? null;
  const companionKeyRef = useRef(companionKey);
  companionKeyRef.current = companionKey;

  const fetchProjects = useCallback(async (
    options: { preserveError?: boolean } = {},
  ): Promise<Project[]> => {
    if (mutationsInFlight.current > 0) {
      // Do not start a snapshot while a create/rename can still change the
      // collection. One authoritative refresh runs when the last mutation
      // settles; callers receive the last honest collection in the meantime.
      refreshQueued.current = true;
      return projectsRef.current;
    }
    const requestVersion = ++listRequestVersion.current;
    if (!getStoredToken() || !companionIds) return [];
    if (companionIds.length === 0) {
      setProjects([]);
      setError('No companions are available to record project provenance.');
      setLoading(false);
      return [];
    }

    setLoading(true);
    if (!options.preserveError) setError(null);
    try {
      const response = await fetch('/api/projects', { headers: buildHeaders() });
      if (!response.ok) throw new Error(`project list failed (${response.status})`);
      const rows = await response.json() as unknown;
      if (!Array.isArray(rows) || rows.some((row) => !isProject(row))) {
        throw new Error('invalid project list');
      }
      const merged = sortProjects(rows as Project[]);
      if (requestVersion === listRequestVersion.current) setProjects(merged);
      return merged;
    } catch (cause) {
      console.error('[useProjects] list failed:', cause);
      if (requestVersion === listRequestVersion.current) {
        // A refresh failure must not erase the last honest collection.
        if (!options.preserveError) setError('Projects could not be loaded. Try again.');
      }
      return [];
    } finally {
      if (requestVersion === listRequestVersion.current) setLoading(false);
    }
  // companionKey is the stable semantic dependency; companionIds is rebuilt
  // only when the server-listed descriptors change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companionKey]);
  const latestFetchProjects = useRef(fetchProjects);
  latestFetchProjects.current = fetchProjects;

  useEffect(() => {
    if (!companionIds) {
      listRequestVersion.current += 1;
      setProjects([]);
      setError(null);
      setLoading(true);
      return;
    }
    void fetchProjects();
  }, [companionIds, fetchProjects]);

  const beginMutation = useCallback(() => {
    // A mutation owns state from this point. Any older list response or error
    // is stale and must not replace the collection after the mutation settles.
    listRequestVersion.current += 1;
    mutationsInFlight.current += 1;
    refreshQueued.current = true;
    setLoading(false);
    setError(null);
  }, []);

  const finishMutation = useCallback(() => {
    mutationsInFlight.current = Math.max(0, mutationsInFlight.current - 1);
    if (mutationsInFlight.current > 0) return;
    if (!refreshQueued.current) return;

    // Whether explicitly requested during the mutation or not, settle on one
    // post-mutation server snapshot. No request that began before or during
    // the mutation can own state after this point.
    refreshQueued.current = false;
    listRequestVersion.current += 1;
    // Reconciliation is background work. A successful mutation already owns
    // an authoritative response, while a failed mutation owns its specific
    // error; the follow-up list must not replace either outcome with a generic
    // refresh error.
    void latestFetchProjects.current({ preserveError: true });
  }, []);

  const createProject = useCallback(async (name: string, identity: string): Promise<Project | null> => {
    const trimmed = name.trim();
    if (!trimmed || !companionIds?.includes(identity)) {
      setError('Choose a valid companion and enter a project name.');
      return null;
    }
    const operationCompanionKey = companionKey;
    beginMutation();
    try {
      const response = await fetch('/api/projects', {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({ name: trimmed, companion: identity }),
      });
      if (!response.ok) throw new Error(`project create failed (${response.status})`);
      const created = await response.json() as unknown;
      if (!isProject(created) || created.identity !== identity) throw new Error('project create identity mismatch');
      if (companionKeyRef.current !== operationCompanionKey) return null;
      setProjects((current) => sortProjects([created, ...current.filter((row) => row.id !== created.id)]));
      setError(null);
      return created;
    } catch (cause) {
      console.error('[useProjects] create failed:', cause);
      setError('The project could not be created. Try again.');
      return null;
    } finally {
      finishMutation();
    }
  }, [beginMutation, companionIds, companionKey, finishMutation]);

  const renameProject = useCallback(async (projectId: string, name: string): Promise<boolean> => {
    const existing = projects.find((project) => project.id === projectId);
    const trimmed = name.trim();
    if (!existing || !trimmed) {
      setError('That project is not available to rename.');
      return false;
    }
    const operationCompanionKey = companionKey;
    beginMutation();
    try {
      const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}`, {
        method: 'PATCH',
        headers: buildHeaders(),
        body: JSON.stringify({ name: trimmed }),
      });
      if (!response.ok) throw new Error(`project rename failed (${response.status})`);
      const updated = await response.json() as unknown;
      if (!isProject(updated) || updated.id !== existing.id || updated.identity !== existing.identity) {
        throw new Error('project rename identity mismatch');
      }
      if (companionKeyRef.current !== operationCompanionKey) return false;
      setProjects((current) => sortProjects(current.map((project) => (
        project.id === projectId ? updated : project
      ))));
      setError(null);
      return true;
    } catch (cause) {
      console.error('[useProjects] rename failed:', cause);
      setError('The project could not be renamed. Try again.');
      return false;
    } finally {
      finishMutation();
    }
  }, [beginMutation, companionKey, finishMutation, projects]);

  // Merges a partial server response (e.g. the WR-0 repository-binding PATCH)
  // onto the existing record — never a wholesale replace. The server actually
  // returns the full row (transcript.getProject() does `SELECT *`), so today
  // every field including created_at/last_activity is present; merge-not-
  // replace is defensive/forward-looking, not a workaround for a response
  // that's missing anything — it means a caller that only has (or only wants
  // to apply) a subset of fields can never accidentally blank out the rest.
  // This is the ONE place `projects` is mutated outside a full list fetch or
  // the create/rename flows above; any caller that needs the central project
  // store to reflect an out-of-band update (Writer's Room binding a project
  // inline) goes through here, not a parallel local refresh.
  // Wrapped in begin/finishMutation like createProject/renameProject so a
  // fetchProjects() response already in flight when this patch lands can't
  // resolve afterward and clobber the freshly-applied binding with a stale
  // snapshot.
  const applyProjectUpdate = useCallback((patch: ProjectPatch) => {
    beginMutation();
    setProjects((current) => sortProjects(current.map((project) => (
      project.id === patch.id ? { ...project, ...patch } : project
    ))));
    finishMutation();
  }, [beginMutation, finishMutation]);

  return {
    projects,
    loading,
    error,
    fetchProjects,
    createProject,
    renameProject,
    applyProjectUpdate,
  };
}
